import React, {useState, useEffect} from "react";
import {Link} from "react-router-dom";
import {ArrowRight} from "lucide-react";
import axios from "axios";
import DOMPurify from "dompurify";
import Hero from "../components/Hero";
import hp0 from "../assets/hp0.png";
import threadsBlack from "../assets/threads-black.png";
import goodreadsBlack from "../assets/goodreads-black.png";

const About: React.FC = () => {
  const [aboutContent, setAboutContent] = useState<string>("");
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get("/api/content-sections/about_hallie");
      if (response.data.success) {
        setAboutContent(response.data.data.content);
      }
    } catch (error) {
      console.error("Error fetching about content:", error);
      // Fallback to default content
      setAboutContent(
        "THIS IS A BLURB ABOUT OUR GIRL. WE LOVE HER. WE STAN HER WORKS. SHE IS A LITERARY GODDESS. YASS QUEEN, SLAY THEM READERS. SLAAAAAAYYYYYY!"
      );
    } finally {
      setContentLoading(false);
    }
  };
  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <Hero showShopButton={true} />

      {/* About Content Section */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-0 sm:px-0 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left side - Author info with image */}
            <div className="bg-primary-300 p-8">
              {/* Image box with same styling as homepage boxes */}
              <div className="bg-primary-300 border-4 border-primary-600 p-4 mb-6 shadow-lg">
                <img
                  src={hp0}
                  alt="Hallie Parker"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Author name and title */}
              <div className="text-center mb-6">
                <h2 className="text-4xl font-bold text-primary-400 mb-2">
                  Hallie Parker
                </h2>
                <span className="text-lg text-primary-800">INDIE AUTHOR</span>
              </div>

              {/* Social media links */}
              <div className="flex justify-center space-x-4">
                <a
                  href="https://www.instagram.com/hallieparkerwrites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-800 hover:text-primary-600 transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@hallieparkerwrites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-800 hover:text-primary-600 transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
                <a
                  href="https://www.threads.com/@hallieparkerwrites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-800 hover:text-primary-600 transition-colors"
                  aria-label="Threads"
                >
                  <img src={threadsBlack} alt="Threads" className="h-6 w-6" />
                </a>
                <a
                  href="https://www.goodreads.com/author/show/23130527.Hallie_Parker"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-800 hover:text-primary-600 transition-colors"
                  aria-label="Goodreads"
                >
                  <img
                    src={goodreadsBlack}
                    alt="Goodreads"
                    className="h-6 w-6"
                  />
                </a>
              </div>
            </div>

            {/* Right side - About text and buttons */}
            <div className="bg-white flex flex-col justify-center h-full py-8">
              <div className="flex-1 flex flex-col justify-center mx-10">
                <h2 className="text-4xl font-bold text-primary-400 mb-6">
                  About Hallie
                </h2>
                <div
                  className="text-xl text-primary-800 leading-relaxed prose prose-xl max-w-none prose-primary"
                  dangerouslySetInnerHTML={{
                    __html: contentLoading
                      ? "Loading..."
                      : DOMPurify.sanitize(
                          aboutContent ||
                            "THIS IS A BLURB ABOUT OUR GIRL. WE LOVE HER. WE STAN HER WORKS. SHE IS A LITERARY GODDESS. YASS QUEEN, SLAY THEM READERS. SLAAAAAAYYYYYY!"
                        ),
                  }}
                />
              </div>

              <div className="flex justify-around mt-8 mb-4">
                <Link
                  to="/projects"
                  className="btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                >
                  Projects
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/shop"
                  className="btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                >
                  Shop
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
